import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, noop } from 'underscore';
import { ActionButton, DefaultButton, List, Panel, PanelType, SearchBox } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { Pagination } from '@bingads-webui-grid/campaign-details-list';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withUiBlock } from '@bingads-webui/uiblocker';

import { AudienceCard } from './audience-card';
import { AudienceEditor } from './audience-editor';
import { AudienceNoDataView } from './audience-no-data';
import { fetchAudienceGroups } from '../model/audience-group';
import { getAudienceSelectorStyle } from '../styles/audience-selector';

const UnstyledAudienceSelector = (props) => {
  const {
    aid,
    cid,
    classes,
    i18n,
    isOpen,
    onClose,
    onAudienceGroupChanged,
    onGenericError,
    originalAudienceGroup,
    wizardPerfMarker,
  } = props;

  const perfMarker = useMemo(
    () => {
      const child = wizardPerfMarker.createChild('Audience_Signal_Selector', { autoEnd: true });
      child.willInit();
      return child;
    },
    [wizardPerfMarker]
  );

  const [isLoading, setIsLoading] = useState(true);

  const [listRef, setListRef] = useState();

  const [allAudienceGroups, setAllAudienceGroups] = useState();
  const [currentAudienceGroupIndex, setCurrentAudienceGroupIndex] = useState();

  const [keyword, setKeyword] = useState('');

  const [showAudienceEditor, setShowAudienceEditor] = useState(false);

  const showNoDataView = useMemo(() => !isLoading && !keyword && isEmpty(allAudienceGroups), [allAudienceGroups, isLoading, keyword]);
  const showNoSearchResultView = useMemo(() => !isLoading && keyword && isEmpty(allAudienceGroups), [allAudienceGroups, isLoading, keyword]);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pageTotal, setPageTotal] = useState(0);

  const onPageChange = useCallback((number) => {
    setPageNumber(number);
  }, []);

  const onPageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPageNumber(1);
  }, []);

  const pager = useMemo(() => ({
    pageNumber,
    pageSize,
    total: pageTotal,
    onPageChange,
    onPageSizeChange,
  }), [onPageChange, onPageSizeChange, pageNumber, pageSize, pageTotal]);

  const onSearch = useCallback((value) => {
    setKeyword(value.trim());
    setPageNumber(1);
  }, []);

  const onClickAudienceGroup = useCallback((theAudienceGroup) => {
    onAudienceGroupChanged(theAudienceGroup);
    onClose();
  }, [onAudienceGroupChanged, onClose]);

  const onEditAudienceGroup = useCallback((index, event) => {
    setCurrentAudienceGroupIndex(index);
    setShowAudienceEditor(true);
    event.stopPropagation();
  }, []);

  const onRenderFooterContent = useCallback(() => (
    <DefaultButton id="panel-cancel-btn" onClick={onClose}>
      {i18n.getString(_TL_('Cancel'))}
    </DefaultButton>
  ), [i18n, onClose]);

  const onRenderActions = useCallback(index => (
    <ActionButton
      iconProps={{ iconName: 'Edit' }}
      onClick={event => onEditAudienceGroup(index, event)}
    >
      {i18n.getString(_TL_('Edit'))}
    </ActionButton>
  ), [i18n, onEditAudienceGroup]);

  const onRenderAudienceGroup = useCallback((theAudienceGroup, index) => (
    <AudienceCard
      audienceGroup={theAudienceGroup}
      i18n={i18n}
      isReadOnlyUser={false}
      onSelectAudienceSignal={onClickAudienceGroup}
      onRenderActions={() => onRenderActions(index)}
      showActions
      enableFocus
    />
  ), [i18n, onClickAudienceGroup, onRenderActions]);

  const fetchData = useCallback(() => {
    perfMarker.willFetchData();
    setIsLoading(true);

    fetchAudienceGroups({
      aid,
      cid,
      keyword,
      top: pageSize,
      skip: (pageNumber - 1) * pageSize,
    })
      .then((data) => {
        perfMarker.willRender();
        setAllAudienceGroups(data.value || []);
        setPageTotal(data['@odata.count'] || 0);
        setIsLoading(false);
      })
      .catch((error) => {
        onGenericError(error);
        perfMarker.done();
      })
      .finally(withUiBlock(listRef));
  }, [aid, cid, keyword, listRef, onGenericError, pageNumber, pageSize, perfMarker]);

  const onGlobalShortcut = useCallback((e) => {
    if (e.key === 'Tab' && !e.shiftKey && e.target.parentElement && e.target.parentElement.className.includes('nav-skip')) {
      e.preventDefault();
      document.getElementsByClassName('ms-Panel-closeButton')[0].focus();
    }
    if (e.key === 'Tab' && e.shiftKey && e.target.className.includes('HelpTopic analysable')) {
      e.preventDefault();
      document.getElementById('panel-cancel-btn').focus();
    }
  }, []);
  useEffect(() => {
    window.addEventListener('keyup', onGlobalShortcut);
    return () => {
      window.removeEventListener('keyup', onGlobalShortcut);
    };
  }, [i18n, onGlobalShortcut]);

  useEffect(() => {
    if (listRef) {
      fetchData();
    }
  }, [fetchData, listRef]);

  useLayoutEffect(() => {
    if (allAudienceGroups) {
      perfMarker.done();
    }
  }, [allAudienceGroups, perfMarker]);

  return (
    <>
      <Panel
        className={classNames(['audience-selector', classes.selectorContainer])}
        headerText={i18n.getString(_TL_('Choose an audience signal'))}
        closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
        isOpen={isOpen}
        onDismiss={onClose}
        onOuterClick={noop}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom
        type={PanelType.medium}
      >
        <div className="audience-description">
          {i18n.getString(_TL_('Choose from your existing audience signals.'))}
        </div>
        {showNoDataView && <AudienceNoDataView i18n={i18n} />}
        {!showNoDataView && <SearchBox
          className="audience-search-box"
          placeholder={i18n.getString(_TL_('Search audience signals'))}
          onSearch={onSearch}
        />}
        {showNoSearchResultView && (
          <div className="audience-no-result">
            <ReactTemplate
              model={{
                keywordTag: text => <b>{text}</b>,
              }}
              template={i18n.getString(_TL_('No results found for {{keyword}}'), { keyword: `{{keywordTag}}${keyword}{{/keywordTag}}` })}
            />
          </div>
        )}
        <div className="audience-list" ref={newRef => setListRef(newRef)} data-is-scrollable>
          {allAudienceGroups && allAudienceGroups.length > 0 && (
            <>
              <List
                items={allAudienceGroups}
                onRenderCell={onRenderAudienceGroup}
              />
              <Pagination {...pager} />
            </>
          )}
        </div>
      </Panel>
      {showAudienceEditor && (
        <AudienceEditor
          audienceGroup={allAudienceGroups[currentAudienceGroupIndex]}
          originalAudienceGroup={originalAudienceGroup}
          i18n={i18n}
          isOpen={showAudienceEditor}
          onAudienceGroupChanged={fetchData}
          onClose={() => setShowAudienceEditor(false)}
          onGenericError={onGenericError}
          wizardPerfMarker={wizardPerfMarker}
        />
      )}
    </>
  );
};

UnstyledAudienceSelector.propTypes = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  classes: PropTypes.shape({
    selectorContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onAudienceGroupChanged: PropTypes.func,
  onGenericError: PropTypes.func,
  originalAudienceGroup: PropTypes.shape({
    Id: PropTypes.number.isRequired,
  }),
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
};

UnstyledAudienceSelector.defaultProps = {
  isOpen: false,
  onClose: noop,
  onAudienceGroupChanged: noop,
  onGenericError: noop,
  originalAudienceGroup: null,
};

export const AudienceSelector = withDefaultStyles(UnstyledAudienceSelector, getAudienceSelectorStyle);
